import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNegocio } from './AliadoDashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Package, Search, Pencil, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const UNITS = ['pieza', 'kg', 'litro', 'paquete', 'caja', 'porción', 'servicio'];

const emptyProduct = {
    name: '', description: '', price: '', compare_price: '', category_id: '__none__',
    sku: '', stock: '', unit: 'pieza', image_url: '', available: true,
    preparation_time: '', allergens: '', tags: '',
};

export default function AliadoProductsPage() {
    const negocio = useNegocio();
    const isFood = ['okfood', 'okrestaurante'].includes(negocio?.super_category);

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterCat, setFilterCat] = useState('__none__');

    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [saving, setSaving] = useState(false);

    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [p, c] = await Promise.all([
                api.get('/me/oknegocio/products'),
                api.get('/me/oknegocio/product-categories'),
            ]);
            setProducts(p.data);
            setCategories(c.data);
        } catch { toast.error('Error al cargar productos'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyProduct);
        setShowForm(true);
    };

    const openEdit = (p) => {
        setEditing(p.id);
        setForm({
            name: p.name, description: p.description, price: p.price, compare_price: p.compare_price,
            category_id: p.category_id || '__none__', sku: p.sku, stock: p.stock,
            unit: p.unit || 'pieza', image_url: p.image_url, available: p.available ?? true,
            preparation_time: p.preparation_time || '', allergens: (p.allergens || []).join(', '),
            tags: (p.tags || []).join(', '),
        });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name) return toast.error('El nombre es requerido');
        if (!form.price && form.price !== 0) return toast.error('El precio es requerido');
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: parseFloat(form.price) || 0,
                compare_price: parseFloat(form.compare_price) || 0,
                stock: parseInt(form.stock) || 0,
                preparation_time: parseInt(form.preparation_time) || 0,
                allergens: form.allergens ? form.allergens.split(',').map(s => s.trim()).filter(Boolean) : [],
                tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
                category_id: form.category_id === '__none__' ? '' : form.category_id,
            };
            if (editing) {
                await api.put(`/me/oknegocio/products/${editing}`, payload);
                toast.success('Producto actualizado');
            } else {
                await api.post('/me/oknegocio/products', payload);
                toast.success('Producto creado');
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/me/oknegocio/products/${deleteId}`);
            toast.success('Producto eliminado');
            setDeleteId(null);
            load();
        } catch { toast.error('Error al eliminar'); }
    };

    const toggleAvailable = async (p) => {
        try {
            await api.put(`/me/oknegocio/products/${p.id}`, { available: !p.available });
            setProducts(prev => prev.map(x => x.id === p.id ? { ...x, available: !x.available } : x));
        } catch { toast.error('Error al actualizar disponibilidad'); }
    };

    const filtered = products.filter(p => {
        const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
        const matchCat = filterCat === '__none__' || p.category_id === filterCat;
        return matchSearch && matchCat;
    });

    const catName = (id) => categories.find(c => c.id === id)?.name || '—';

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><Package className="h-5 w-5 text-orange-600" /> Productos</h1>
                    <p className="text-sm text-muted-foreground">{products.length} producto(s)</p>
                </div>
                <Button size="sm" onClick={openCreate} className="bg-orange-600 hover:bg-orange-700">
                    <Plus className="h-4 w-4 mr-1" /> Agregar producto
                </Button>
            </div>

            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
                <div className="relative flex-1 min-w-36">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input className="pl-8 h-8 text-xs" placeholder="Buscar productos…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                {categories.length > 0 && (
                    <Select value={filterCat} onValueChange={setFilterCat}>
                        <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Categoría" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">Todas</SelectItem>
                            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl border bg-card p-8 text-center">
                    <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground">
                        {search || filterCat !== '__none__' ? 'No se encontraron productos.' : 'Aún no tienes productos. ¡Agrega el primero!'}
                    </div>
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                    {filtered.map(p => (
                        <div key={p.id} className="rounded-2xl border bg-card p-3 flex gap-3">
                            {p.image_url
                                ? <img src={p.image_url} alt={p.name} className="h-16 w-16 rounded-xl object-cover shrink-0 border" />
                                : <div className="h-16 w-16 rounded-xl bg-muted flex items-center justify-center shrink-0 text-2xl">📦</div>
                            }
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-1">
                                    <div className="font-medium text-sm truncate">{p.name}</div>
                                    <Switch checked={p.available} onCheckedChange={() => toggleAvailable(p)} className="shrink-0 scale-75 mt-0.5" />
                                </div>
                                {categories.length > 0 && p.category_id && (
                                    <div className="text-[10px] text-muted-foreground">{catName(p.category_id)}</div>
                                )}
                                <div className="text-sm font-bold text-orange-700 mt-0.5">
                                    {fmt(p.price)}
                                    {p.compare_price > 0 && <span className="text-xs line-through text-muted-foreground ml-1">{fmt(p.compare_price)}</span>}
                                </div>
                                {!p.available && <Badge variant="outline" className="text-[9px] mt-0.5">No disponible</Badge>}
                                <div className="flex gap-1 mt-2">
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="h-6 px-2 text-xs">
                                        <Pencil className="h-3 w-3 mr-1" /> Editar
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(p.id)} className="h-6 px-2 text-xs text-destructive hover:bg-destructive/10">
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Form dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium">Nombre *</label>
                            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" placeholder="Ej. Hamburguesa clásica" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Descripción</label>
                            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 text-sm" rows={2} placeholder="Descripción del producto…" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium">Precio *</label>
                                <Input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} className="mt-1" placeholder="0.00" min="0" step="0.01" />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Precio tachado</label>
                                <Input type="number" value={form.compare_price} onChange={e => setForm(p => ({ ...p, compare_price: e.target.value }))} className="mt-1" placeholder="0.00" min="0" step="0.01" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium">Categoría</label>
                                <Select value={form.category_id} onValueChange={v => setForm(p => ({ ...p, category_id: v }))}>
                                    <SelectTrigger className="mt-1 text-xs"><SelectValue placeholder="Sin categoría" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">Sin categoría</SelectItem>
                                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-medium">Unidad</label>
                                <Select value={form.unit} onValueChange={v => setForm(p => ({ ...p, unit: v }))}>
                                    <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium">SKU / Código</label>
                                <Input value={form.sku} onChange={e => setForm(p => ({ ...p, sku: e.target.value }))} className="mt-1" placeholder="ABC-001" />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Stock</label>
                                <Input type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: e.target.value }))} className="mt-1" placeholder="0" min="0" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-medium">URL de imagen</label>
                            <Input value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} className="mt-1" placeholder="https://…" />
                        </div>
                        {isFood && (
                            <>
                                <div>
                                    <label className="text-xs font-medium">Tiempo de preparación (min)</label>
                                    <Input type="number" value={form.preparation_time} onChange={e => setForm(p => ({ ...p, preparation_time: e.target.value }))} className="mt-1" placeholder="15" min="0" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium">Alérgenos (separados por coma)</label>
                                    <Input value={form.allergens} onChange={e => setForm(p => ({ ...p, allergens: e.target.value }))} className="mt-1" placeholder="gluten, lactosa, mariscos…" />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="text-xs font-medium">Etiquetas (separadas por coma)</label>
                            <Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} className="mt-1" placeholder="vegetariano, sin gluten…" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch id="avail" checked={form.available} onCheckedChange={v => setForm(p => ({ ...p, available: v }))} />
                            <label htmlFor="avail" className="text-xs font-medium">Disponible para pedidos</label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                            {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear producto'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>¿Eliminar producto?</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
